import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ACCOUNT_DETAIL_FONT_SX } from '../../../components/AccountDetailPolicy/accountDetailTypography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDispatch, useSelector } from 'react-redux';
import { createComplianceWorkload, clearAllComplianceMessages, getComplianceTags } from '../../../store/compliance/complianceSlice';
import Autocomplete from '@mui/material/Autocomplete';
import { getComplianceTags as getComplianceTagsApi } from '../../../helpers/auth_aws_helper';

const CreateWorkloadModal = ({ open, onClose, accounts, onSuccess }) => {
  const dispatch = useDispatch();
  const [selectedAccount, setSelectedAccount] = useState(accounts?.[0]?.accountNumber || '');
  const [workloadName, setWorkloadName] = useState('');
  const [tags, setTags] = useState([{ key: '', value: '' }]);
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Tag API local cache and loading/error state
  const [tagKeysByAccount, setTagKeysByAccount] = useState({}); // { accountNumber: [keys] }
  const [tagValuesByAccountAndKey, setTagValuesByAccountAndKey] = useState({}); // { 'account:key': [values] }
  const [loadingTagKeys, setLoadingTagKeys] = useState(false);
  const [loadingTagValues, setLoadingTagValues] = useState({}); // { 'account:key': bool }
  const [tagApiError, setTagApiError] = useState('');

  const { createComplianceWorkloadMessage, createComplianceWorkloadError, isCreatingComplianceWorkload } = useSelector(state => state.compliance);

  // Validation
  const errors = {
    account: !selectedAccount,
    workloadName: !workloadName.trim(),
    tagKey1: !tags[0].key.trim(),
    tagValue1: !tags[0].value.trim(),
    tagKey2: tags[1] ? !tags[1].key.trim() && !!tags[1].value.trim() : false,
    tagValue2: tags[1] ? !tags[1].value.trim() && !!tags[1].key.trim() : false,
  };
  const hasError = Object.values(errors).some(Boolean);

  const handleBlur = (field) => setTouched(t => ({ ...t, [field]: true }));

  const handleTagChange = (idx, field, value) => {
    setTags(prev => prev.map((tag, i) => {
      if (i !== idx) return tag;
      if (field === 'key') {
        return { ...tag, key: value, value: '' };
      }
      return { ...tag, [field]: value };
    }));
  };

  const handleAddTag = () => {
    if (tags.length < 2) setTags([...tags, { key: '', value: '' }]);
  };

  const handleRemoveTag = () => {
    if (tags.length > 1) setTags(tags.slice(0, 1));
  };

  const handleCreate = () => {
    if (hasError) return;
    const payload = {
      scheduleName: workloadName,
      scheduleType: 'on_demand',
      frequency: null,
      resourceScopeType: 'tag_based',
      tag: tags
        .filter(tag => tag.key.trim() && tag.value.trim())
        .map(tag => ({ key: tag.key.trim(), value: tag.value.trim() })),
    };
    dispatch(createComplianceWorkload({ accountNumber: selectedAccount, ...payload }));
  };

  // Fetch tag keys for account
  const fetchTagKeys = async (accountNumber) => {
    if (!accountNumber || tagKeysByAccount[accountNumber]) return;
    setLoadingTagKeys(true);
    setTagApiError('');
    try {
      const res = await getComplianceTagsApi({ account_number: accountNumber });
      const keys = Array.isArray(res.keys) ? res.keys : [];
      setTagKeysByAccount(prev => ({ ...prev, [accountNumber]: keys }));
    } catch (err) {
      setTagApiError(err?.message || 'Failed to fetch tag keys');
    } finally {
      setLoadingTagKeys(false);
    }
  };

  // Fetch tag values for (account, key)
  const fetchTagValues = async (accountNumber, key) => {
    const cacheKey = `${accountNumber}:${key}`;
    if (!accountNumber || !key || tagValuesByAccountAndKey[cacheKey]) return;
    setLoadingTagValues(prev => ({ ...prev, [cacheKey]: true }));
    setTagApiError('');
    try {
      const res = await getComplianceTagsApi({ account_number: accountNumber, tag_key: key });
      // Response shape: { "Created By": [ ... ] }
      const values = Array.isArray(res[key]) ? res[key] : [];
      setTagValuesByAccountAndKey(prev => ({ ...prev, [cacheKey]: values }));
    } catch (err) {
      setTagApiError(err?.message || 'Failed to fetch tag values');
    } finally {
      setLoadingTagValues(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // On modal open or account change, fetch tag keys
  useEffect(() => {
    if (open && selectedAccount) {
      fetchTagKeys(selectedAccount);
    }
    // eslint-disable-next-line
  }, [open, selectedAccount]);

  // On tag key change, fetch tag values for that key
  useEffect(() => {
    tags.forEach((tag, idx) => {
      if (tag.key && selectedAccount) {
        fetchTagValues(selectedAccount, tag.key);
      }
    });
    // eslint-disable-next-line
  }, [tags.map(t => t.key).join(','), selectedAccount]);

  // Reset tag cache and errors on modal close
  useEffect(() => {
    if (!open) {
      setTagKeysByAccount({});
      setTagValuesByAccountAndKey({});
      setLoadingTagKeys(false);
      setLoadingTagValues({});
      setTagApiError('');
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      // Reset form fields and state when modal opens
      setSelectedAccount(accounts?.[0]?.accountNumber || '');
      setWorkloadName('');
      setTags([{ key: '', value: '' }]);
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      dispatch(clearAllComplianceMessages());
    }
    if (!open) {
      setSnackbar({ open: false, message: '', severity: 'error' });
      dispatch(clearAllComplianceMessages());
    }
    // eslint-disable-next-line
  }, [open, accounts]);

  useEffect(() => {
    if (createComplianceWorkloadMessage) {
      if (onSuccess) onSuccess(createComplianceWorkloadMessage);
      dispatch(clearAllComplianceMessages());
      onClose();
    } else if (createComplianceWorkloadError) {
      setSnackbar({ open: true, message: createComplianceWorkloadError, severity: 'error' });
      dispatch(clearAllComplianceMessages());
    }
    // eslint-disable-next-line
  }, [createComplianceWorkloadMessage, createComplianceWorkloadError]);

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="create-workload-modal"
        aria-describedby="modal-for-creating-workload"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            bgcolor: 'background.paper',
            borderRadius: '10px',
            boxShadow: 24,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            ...ACCOUNT_DETAIL_FONT_SX,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F5F5F5',
              padding: '16px 24px',
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              borderBottom: '1px solid #E0E0E0',
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Create Workload
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          {/* Content */}
          <Box sx={{ p: 3 }}>
            <FormControl fullWidth sx={{ mb: 3, minWidth: 250, maxWidth: 320 }} error={touched.account && errors.account}>
              <InputLabel id="account-select-label">Account *</InputLabel>
              <Select
                labelId="account-select-label"
                value={selectedAccount}
                label="Account *"
                onChange={e => setSelectedAccount(e.target.value)}
                onBlur={() => handleBlur('account')}
                sx={{ width: '100%' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      minWidth: 250,
                      maxWidth: 320,
                    },
                  },
                }}
              >
                {accounts?.map(acc => (
                  <MenuItem key={acc.accountNumber} value={acc.accountNumber}>
                    <Box sx={{ maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {acc.accountName} ({acc.accountNumber})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {touched.account && errors.account && (
                <Typography color="error" variant="caption">Account is required</Typography>
              )}
            </FormControl>
            <TextField
              label="Workload Name *"
              value={workloadName}
              onChange={e => setWorkloadName(e.target.value)}
              onBlur={() => handleBlur('workloadName')}
              fullWidth
              sx={{ mb: 0.5 }}
              error={touched.workloadName && errors.workloadName}
              helperText={touched.workloadName && errors.workloadName ? 'Workload Name is required' : ''}
              inputProps={{ maxLength: 128 }}
            />
            <Typography variant="caption" sx={{ color: '#9e9e9e', mt: 0, mb: 1, ml: 0.5, display: 'block', lineHeight: 1.2 }}>
              A workload name is unique per account.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
              {tags.map((tag, idx) => {
                const tagKeys = tagKeysByAccount[selectedAccount] || [];
                const cacheKey = `${selectedAccount}:${tag.key}`;
                const tagValues = tagValuesByAccountAndKey[cacheKey] || [];
                return (
                  <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Autocomplete
                      freeSolo
                      options={tagKeys}
                      loading={loadingTagKeys}
                      value={tag.key}
                      onInputChange={(_, newValue) => handleTagChange(idx, 'key', newValue)}
                      onBlur={() => handleBlur(`tagKey${idx + 1}`)}
                      fullWidth
                      ListboxProps={{
                        style: {
                          width: 400,
                          maxHeight: 200,
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span style={{ display: 'inline-block', minWidth: 600, whiteSpace: 'nowrap' }}>{option}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Tag Key${idx === 0 ? ' *' : ''}`}
                          sx={{ flex: 1, minWidth: 0, maxWidth: 240 }}
                          error={touched[`tagKey${idx + 1}`] && (idx === 0 ? errors.tagKey1 : errors.tagKey2)}
                          helperText={touched[`tagKey${idx + 1}`] && (idx === 0 ? errors.tagKey1 ? 'Tag Name is required' : '' : errors.tagKey2 ? 'Tag Name is required if Tag Value is filled' : '')}
                          inputProps={{ ...params.inputProps, maxLength: 64 }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingTagKeys ? (
                                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Autocomplete
                      freeSolo
                      options={tagValues}
                      loading={!!loadingTagValues[cacheKey]}
                      value={tag.value}
                      onInputChange={(_, newValue) => handleTagChange(idx, 'value', newValue)}
                      onBlur={() => handleBlur(`tagValue${idx + 1}`)}
                      fullWidth
                      ListboxProps={{
                        style: {
                          width: 400,
                          maxHeight: 200,
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span style={{ display: 'inline-block', minWidth: 600, whiteSpace: 'nowrap' }}>{option}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Tag Value${idx === 0 ? ' *' : ''}`}
                          sx={{ flex: 1, minWidth: 0, maxWidth: 240 }}
                          error={touched[`tagValue${idx + 1}`] && (idx === 0 ? errors.tagValue1 : errors.tagValue2)}
                          helperText={touched[`tagValue${idx + 1}`] && (idx === 0 ? errors.tagValue1 ? 'Tag Value is required' : '' : errors.tagValue2 ? 'Tag Value is required if Tag Name is filled' : '')}
                          inputProps={{ ...params.inputProps, maxLength: 128 }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {!!loadingTagValues[cacheKey] ? (
                                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {idx === 0 && tags.length < 2 ? (
                        <IconButton onClick={handleAddTag} sx={{ color: '#1976d2' }} aria-label="Add tag">
                          <AddCircleOutlineIcon />
                        </IconButton>
                      ) : idx === 1 ? (
                        <IconButton onClick={handleRemoveTag} sx={{ color: '#d32f2f' }} aria-label="Remove tag">
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      ) : (
                        <IconButton sx={{ opacity: 0, pointerEvents: 'none' }}>
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {/* Tag API error snackbar inside modal, below tags */}
            {tagApiError && (
              <Snackbar
                open={!!tagApiError}
                autoHideDuration={6000}
                onClose={() => setTagApiError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ position: 'relative', mt: 2 }}
              >
                <Alert onClose={() => setTagApiError('')} severity="error" sx={{ width: '100%' }}>
                  {tagApiError}
                </Alert>
              </Snackbar>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={hasError || isCreatingComplianceWorkload}
                onClick={handleCreate}
                startIcon={isCreatingComplianceWorkload ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isCreatingComplianceWorkload ? 'Creating...' : 'Create'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateWorkloadModal; 